import { Grid } from '@mui/material';
import { sameMembers } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { GeoFence } from '../domain/geoFence/GeoFence';
import Text from './Text';

interface GeoFencesSelectorProps {
  label?: React.ReactElement;
  geoFences: GeoFence[];
  size?: `small` | `large`;
  selectedGeoFenceIds?: string[];
  selectButtonStyles?: React.CSSProperties;
  noSelectedZonesText?: React.ReactElement;
  optionsWidth?: number;
  handleUpdate: (geoFences: string[]) => void;
}

export const GeoFencesSelector = ({
  label,
  geoFences,
  size,
  selectedGeoFenceIds = [],
  selectButtonStyles = {},
  noSelectedZonesText,
  handleUpdate,
}: GeoFencesSelectorProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedGeoFenceIds);

  useEffect(() => {
    if (!sameMembers(selectedIds, selectedGeoFenceIds)) {
      setSelectedIds(selectedGeoFenceIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeoFenceIds]);

  const renderZoneOptions = (): RoutalSelectMultiSelectOption[] =>
    geoFences.map(
      (geoFence): RoutalSelectMultiSelectOption => ({
        key: geoFence.id,
        listItemStyles: {
          minWidth: `auto!important`,
          maxWidth: `auto!important`,
        },
        checked: selectedIds?.includes(geoFence.id),
        hideCheckIcon: false,
        closeOnClick: false,
        onClick: () => {
          const geoFenceSet = new Set(selectedIds);
          if (geoFenceSet.has(geoFence.id)) {
            geoFenceSet.delete(geoFence.id);
          } else {
            geoFenceSet.add(geoFence.id);
          }
          setSelectedIds(Array.from(geoFenceSet));
        },
        content: (
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item>
              <div
                style={{
                  display: `flex`,
                  flexDirection: `row`,
                  alignItems: `center`,
                  justifyContent: `center`,
                  padding: `2px 8px`,
                  margin: `0 2px`,
                  backgroundColor: `${geoFence.color.value}`,
                  color: RoutalPalette.neutral00,
                  width: `14px`,
                  height: `18px`,
                  borderRadius: `4px`,
                }}
              >
                <Text tagName="span" style={{ cursor: `pointer`, color: RoutalPalette.neutral00, fontSize: 10 }}>
                  {geoFence.getShortLabel()}
                </Text>
              </div>
            </Grid>

            <Text
              style={{
                marginLeft: `8px`,
                cursor: `pointer`,
                textOverflow: `ellipsis`,
                overflow: `hidden`,
                whiteSpace: `nowrap`,
              }}
            >
              {geoFence.label}
            </Text>
          </Grid>
        ),
      })
    );

  return (
    <>
      {label ?? null}
      <RoutalSelect
        id="geo_fences-selector"
        containerStyle={{
          margin: `6px 0`,
        }}
        style={{
          // backgroundColor: `transparent`,
          // border: `1px solid ${RoutalPalette.primary00}`,
          // color: RoutalPalette.text,
          ...selectButtonStyles,
        }}
        popoverProps={{
          onBlur: () => {
            if (!sameMembers(selectedIds, selectedGeoFenceIds)) {
              handleUpdate(selectedIds);
            }
          },
          options:
            geoFences.length > 0
              ? renderZoneOptions()
              : [
                  {
                    key: `no_zones`,
                    checked: false,
                    onClick: () => {},
                    listItemStyles: {
                      minWidth: `auto!important`,
                      maxWidth: `auto!important`,
                    },
                    content: <Text variant="label" intlId="planner.stops.no_zones" />,
                  },
                ],
        }}
      >
        <Grid container direction="row">
          {(selectedIds ?? []).length === 0
            ? noSelectedZonesText ?? <Text variant="label" intlId="geo_fences.empty" />
            : null}
          {(selectedIds ?? []).length > 0 &&
            selectedIds!.map((gfId: string, index: number) => {
              if (index >= 6) {
                return (
                  <span
                    style={{
                      lineHeight: `0px`,
                      display: `flex`,
                      flexDirection: `row`,
                      alignItems: `flex-end`,
                      paddingBottom: `6px`,
                    }}
                  >
                    ...
                  </span>
                );
              }

              const geoFence = geoFences.find((el) => el.id === gfId);

              if (geoFence) {
                return (
                  <RoutalTooltip title={geoFence.label} overlayInnerStyle={{ minWidth: `40px` }}>
                    <div
                      style={{
                        padding: `2px 8px`,
                        margin: `0 2px`,
                        backgroundColor: `${geoFence.color.value}`,
                        color: RoutalPalette.neutral00,
                        border: `1px solid ${geoFence.color.value}`,
                        borderRadius: `4px`,
                        width: `24px`,
                        textAlign: `center`,
                      }}
                    >
                      {geoFence.getShortLabel()}
                    </div>
                  </RoutalTooltip>
                );
              }
              return null;
            })}
        </Grid>
      </RoutalSelect>
    </>
  );
};
